/**
 * Export-time bridge for two things html2canvas v1.x does not render.
 *
 * 1. `object-fit`. html2canvas draws every <img> stretched to its box and
 *    ignores `object-fit` entirely, so a photograph shown as `cover` in a
 *    frame of another shape — the normal case for a template photo — is
 *    exported squeezed. `inlineObjectFitForExport` gives each such <img> in
 *    the CLONE the explicit box `object-fit` would have produced (position,
 *    size, no fit). Its parent already clips it (overflow hidden + radius),
 *    which html2canvas does honour.
 *
 * 2. `filter` on an inline <svg>. html2canvas rasterizes an inline SVG by
 *    serializing the node into an image; a CSS `filter` on the SVG root
 *    (how a shape's drop shadow is drawn) makes that image paint an opaque
 *    box over whatever lies behind the shape. The shadow cannot be exported
 *    this way at all, so `stripSvgRootFiltersForExport` removes the filter
 *    from the clone: the shape exports clean, without its shadow.
 *
 * Both functions only mutate the cloned tree. The live editor is untouched.
 */

/** The box `object-fit` gives a w×h replaced element whose content is iw×ih. */
export function objectFitBox(fit, w, h, iw, ih) {
  if (!(w > 0 && h > 0 && iw > 0 && ih > 0)) return null;
  let scale;
  if (fit === 'cover') scale = Math.max(w / iw, h / ih);
  else if (fit === 'contain') scale = Math.min(w / iw, h / ih);
  else if (fit === 'none') scale = 1;
  else if (fit === 'scale-down') scale = Math.min(1, w / iw, h / ih);
  else return null;                       // 'fill' (and unknown values) need no help
  const width = iw * scale;
  const height = ih * scale;
  // object-position defaults to 50% 50%; the editor never sets another.
  return { left: (w - width) / 2, top: (h - height) / 2, width, height };
}

/**
 * Pair every cloned <img> with its live counterpart BEFORE other export steps
 * replace or move nodes (the clip-path bridge swaps masked images for
 * canvases). Document order is identical in a fresh clone.
 */
export function pairImagesForExport(clonedRoot, liveRoot) {
  if (!clonedRoot || !liveRoot || !clonedRoot.querySelectorAll || !liveRoot.querySelectorAll) return [];
  const cloned = Array.from(clonedRoot.querySelectorAll('img'));
  const live = Array.from(liveRoot.querySelectorAll('img'));
  if (cloned.length !== live.length) return [];
  return cloned.map((img, i) => ({ cloned: img, live: live[i] }));
}

/**
 * @param {Array<{cloned: HTMLImageElement, live: HTMLImageElement}>} pairs from `pairImagesForExport`
 * @returns {number} how many images were given an explicit box
 */
export function inlineObjectFitForExport(pairs) {
  let changed = 0;
  (pairs || []).forEach(({ cloned, live }) => {
    // An image another step has already replaced (a masked photo, now a canvas) is no longer in the tree.
    if (!cloned || !live || !cloned.parentNode || !cloned.isConnected) return;
    const view = live.ownerDocument && live.ownerDocument.defaultView;
    const computed = view && view.getComputedStyle ? view.getComputedStyle(live) : null;
    const fit = (computed && computed.objectFit) || (live.style && live.style.objectFit) || 'fill';
    if (fit === 'fill') return;
    // Layout size in the canvas's own pixels: offsetWidth is not affected by the editor's zoom transform.
    const w = live.offsetWidth;
    const h = live.offsetHeight;
    const box = objectFitBox(fit, w, h, live.naturalWidth, live.naturalHeight);
    if (!box) return;
    const s = cloned.style;
    s.position = 'absolute';
    s.left = `${box.left}px`;
    s.top = `${box.top}px`;
    s.width = `${box.width}px`;
    s.height = `${box.height}px`;
    s.maxWidth = 'none';
    s.maxHeight = 'none';
    s.objectFit = 'fill';
    changed += 1;
  });
  return changed;
}

/** @returns {number} how many inline SVG roots had a CSS filter removed */
export function stripSvgRootFiltersForExport(clonedRoot) {
  if (!clonedRoot || !clonedRoot.querySelectorAll) return 0;
  let changed = 0;
  clonedRoot.querySelectorAll('svg').forEach((svg) => {
    const filter = svg.style && svg.style.filter;
    if (!filter || filter === 'none') return;
    svg.style.filter = 'none';
    changed += 1;
  });
  return changed;
}
